import { AddOnItemIdMap, AddOnItemMap, AddOnMonthlyPriceMap, AdditionalUserItemIdMap, BillingOption, billingOptionForSubscription, ChargebeeCheckoutItem, ChargebeeSubscriptionInfo, EstimateSummary, FeatureTierLevel, FeatureTierMonthlyPriceMap, KlavioEventPropertyKey, KlayvioEvents, OrganizationTeamMember, StandardProAddOns, TierItemIdMap, seatTotalForPeriod } from '../types';
import { LitElement, PropertyValueMap, css, html } from 'lit';
import { checkoutSubscription, getChargebeeEstimate } from '../helpers/chargebee';
import { customElement, property, state } from 'lit/decorators';
import { delay, isDefined, show_confetti } from '../helpers/utils';
import { detectMobileViewport, inIframe } from '../helpers/device';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { getMainPage } from '../helpers/page-name';
import { logEventWithKlayvio } from '../helpers/api/api';
import { renderNonprofitDiscountNote } from '../helpers/nonprofit-note';
import organizationInfo from '../helpers/state/organization-info';
import subscriptionInfo from '../helpers/state/subscription-info';

@customElement('cart-component')
export class CartComponent extends LitElement {

    @property({ type: Boolean })
    mini: boolean = false;

    @property({ type: Boolean })
    in_embed: boolean = false;

    @property({ type: Boolean })
    button_only: boolean = false;

    @property({ type: Array })
    availableStandardAddons: StandardProAddOns[] = [StandardProAddOns.IntegrationAutomation, StandardProAddOns.BestOf, StandardProAddOns.LeadGeneration, StandardProAddOns.PasswordPaywall, StandardProAddOns.PassportChallenge, StandardProAddOns.Guide, StandardProAddOns.ScavengerHunt, StandardProAddOns.CustomDomain, StandardProAddOns.BulletinBoard];

    @property({ type: Boolean })
    current_team_users: number = 1;

    @state()
    currentSubscription: ChargebeeSubscriptionInfo | undefined;

    @state()
    billingFrequency: BillingOption = BillingOption.Annual;

    @state()
    estimate?: EstimateSummary;

    @state()
    planType: string = 'haveItAll';

    @state()
    userCount: number = 1;

    @state()
    totalPrice: number = 0;

    @state()
    billedTotalPrice: number = 0;

    @state()
    isChanged: boolean = false;

    @state()
    selectedItemsForCheckout: ChargebeeCheckoutItem[] = [];

    @state()
    selectedStandardAddOns: StandardProAddOns[] = [];

    @state()
    currentTeamUsers: OrganizationTeamMember[] = [];

    @state()
    currentNumUsers: number = 1;

    @state()
    showAllAddons: boolean = false;

    @state()
    isReady: boolean = false;

    // Set when a Premium upsell (e.g. a locked feature) pre-selects the bundle,
    // so the lazily-run startup() doesn't overwrite it with the plan derived
    // from the current subscription.
    private premiumPreselected: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindSubscriptionInfo = new StateController(this, subscriptionInfo);

    private intersectionObserver?: IntersectionObserver;

    protected firstUpdated() {
        // Defer startup until the component is actually visible (e.g. dialog is opened).
        // This avoids firing Chargebee estimate calls for every hidden upgrade-dialog in the app.
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.intersectionObserver = new IntersectionObserver(async (entries) => {
            if (entries.some(e => e.isIntersecting)) {
                this.intersectionObserver?.disconnect();
                if (accountInfo.isReady) {
                    await this.startup();
                } else {
                    await delay(1000).then(async () => {
                        await this.startup();
                    });
                }
            }
        });
        this.intersectionObserver.observe(this);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.intersectionObserver?.disconnect();
    }

    protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
        // if selected add ons or feature or ter changes, update pricing
        if (_changedProperties.has('selectedStandardAddOns') || _changedProperties.has('planType') || _changedProperties.has('billingFrequency') || _changedProperties.has('userCount')) {
            if (this.isReady) {
                try{
                    await this.updatePricing();
                } catch (error) {
                    console.error(error);
                }
            }
        }
    }

    private async startup() {
        this.currentSubscription = subscriptionInfo.subscription;
        this.billingFrequency = billingOptionForSubscription(this.currentSubscription?.billing_period, this.currentSubscription?.billing_period_unit);
        const current_add_ons = this.currentSubscription?.subscription_items?.map((item) => item.item_price_id) ?? [];
        if (current_add_ons.includes(AddOnItemIdMap[StandardProAddOns.HaveItAll].Monthly || AddOnItemIdMap[StandardProAddOns.HaveItAll].Annual)) {
            this.planType = 'haveItAll';
            this.selectedStandardAddOns = [StandardProAddOns.HaveItAll];
        } else {
            this.planType = 'aLaCarte';
            const addons = current_add_ons.map((item) => {
                try {
                    return AddOnItemMap[item];
                } catch (error) {
                    console.error(`Error processing item: ${item}`, error);
                    return null; // or handle the error as needed
                }
            }).filter(addon => addon !== null && addon !== undefined) as StandardProAddOns[];
            this.selectedStandardAddOns = addons;

        }
        if (organizationInfo.organization) {
            this.currentNumUsers = organizationInfo.chargeableMembers.length ?? 1;
            this.userCount = subscriptionInfo.number_of_seats ?? organizationInfo.chargeableMembers.length ?? 1;
        }
        if (this.premiumPreselected) {
            this.planType = 'haveItAll';
            this.selectedStandardAddOns = [StandardProAddOns.HaveItAll];
        }
        this.isReady = true;
        await this.updatePricing();
    }

    private async changeUserCount(delta: number) {
        let newCount =
            this.userCount + delta;

        if (newCount < 1) {
            newCount = 1;
        }

        if (newCount < this.currentNumUsers) {
            getMainPage().errorToastElement.showToastAndThrow(`You currently have ${this.currentNumUsers} on your team.`);
        }

        this.userCount = newCount;
        await this.updatePricing();
    }

    private async updateToggleState(billingOption: BillingOption) {
        this.billingFrequency = billingOption;
        await this.updatePricing();
    }

    private async updatePlanToggleState() {
        this.planType = this.planType === 'haveItAll' ? 'aLaCarte' : 'haveItAll';
        if (this.planType === 'haveItAll') {
            this.selectedStandardAddOns  = [StandardProAddOns.HaveItAll];
        } else {
            this.selectedStandardAddOns = [];
        }
        this.requestUpdate();
        await this.updatePricing();
    }

    private async goToBuildYourOwn() {
        this.planType = 'aLaCarte';
        this.selectedStandardAddOns  = [];
        await this.updatePricing();
    }

    private async getLoadedEstimate() {
        const is_reactivation = this.currentSubscription?.status === 'cancelled';
        const subscription_id = subscriptionInfo.subscription_id ?? undefined;
        const customer_id = subscriptionInfo.subscription_customer_id ?? undefined;
        this.estimate = await getChargebeeEstimate(subscription_id, this.selectedItemsForCheckout, customer_id, is_reactivation);
    };

    async updatePricing() {
        const userCount = parseInt(this.userCount.toString());
        const isExistingMultiSubscription = this.currentSubscription?.subscription_items !== undefined && this.currentSubscription.num_users !== undefined && this.currentSubscription.num_users > 1;
        // Work in billed-period dollars (what Chargebee actually charges) and derive
        // the per-month display from it, so the two can never drift apart.
        const periodMonths = this.billingFrequency === BillingOption.Annual ? 12 : this.billingFrequency === BillingOption.Quarterly ? 3 : 1;
        const basePrice = (this.billingFrequency === BillingOption.Monthly ? FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Monthly :
            this.billingFrequency === BillingOption.Annual ? FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Annual :
            FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Quarterly) ?? 0;
        let additionalPrice = 0;

        // The plan covers the first seat only; the rest bill through the additional-user add-on below.
        this.selectedItemsForCheckout = [{
            item_price_id: this.billingFrequency === BillingOption.Monthly ? TierItemIdMap[FeatureTierLevel.ProBase].Monthly ?? '' :
                this.billingFrequency === BillingOption.Annual ? TierItemIdMap[FeatureTierLevel.ProBase].Annual ?? '' :
                TierItemIdMap[FeatureTierLevel.ProBase].Quarterly ?? '',
            quantity: 1
        }];

        if (this.planType === 'haveItAll') {
            const haveItAllPrice = AddOnMonthlyPriceMap[StandardProAddOns.HaveItAll];
            const haveItAllItemId = AddOnItemIdMap[StandardProAddOns.HaveItAll];

            // remove all other addons from selectedItemsForCheckout
            this.selectedItemsForCheckout = this.selectedItemsForCheckout.filter(item => item.item_price_id === (this.billingFrequency === BillingOption.Monthly ? TierItemIdMap[FeatureTierLevel.ProBase].Monthly : this.billingFrequency === BillingOption.Annual ? TierItemIdMap[FeatureTierLevel.ProBase].Annual : TierItemIdMap[FeatureTierLevel.ProBase].Quarterly));

            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (haveItAllPrice && haveItAllItemId) {
                additionalPrice = this.billingFrequency === BillingOption.Monthly ? haveItAllPrice.Monthly :
                    this.billingFrequency === BillingOption.Annual ? haveItAllPrice.Annual : haveItAllPrice.Quarterly;
                this.selectedItemsForCheckout.push({
                    item_price_id: this.billingFrequency === BillingOption.Monthly ? haveItAllItemId.Monthly : this.billingFrequency === BillingOption.Annual ? haveItAllItemId.Annual : haveItAllItemId.Quarterly,
                    quantity: isExistingMultiSubscription ? userCount : 1
                });
            } else {
                console.error('Price or Item ID for "Have It All" plan is missing.');
            }
        } else {
            // Calculate 'A La Carte' pricing
            for (const addon of this.selectedStandardAddOns) {
                // if the addon is alread in the selected items for checkout, remove it
                const existingItem = this.selectedItemsForCheckout.find(item => item.item_price_id === (this.billingFrequency === BillingOption.Monthly ? AddOnItemIdMap[addon].Monthly : this.billingFrequency === BillingOption.Annual ? AddOnItemIdMap[addon].Annual : AddOnItemIdMap[addon].Quarterly));
                if (existingItem) {
                    this.selectedItemsForCheckout = this.selectedItemsForCheckout.filter(item => item.item_price_id !== existingItem.item_price_id);
                }
                // if have it all is in this.selectedItemsForCheckout, remove it\
                if (this.selectedStandardAddOns.includes(StandardProAddOns.HaveItAll)) {
                    this.selectedStandardAddOns = this.selectedStandardAddOns.filter(item => item !== StandardProAddOns.HaveItAll);
                }

                const addonPrice = AddOnMonthlyPriceMap[addon];
                const addonItemId = AddOnItemIdMap[addon];

                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                if (addonPrice && addonItemId) {
                    additionalPrice += this.billingFrequency === BillingOption.Monthly ? Number(addonPrice.Monthly) : this.billingFrequency === BillingOption.Annual ? Number(addonPrice.Annual) : Number(addonPrice.Quarterly);
                    this.selectedItemsForCheckout.push({
                        item_price_id: this.billingFrequency === BillingOption.Monthly ? addonItemId.Monthly : this.billingFrequency === BillingOption.Annual ? addonItemId.Annual : addonItemId.Quarterly,
                        quantity: isExistingMultiSubscription ? userCount : 1
                    });
                } else {
                    console.error(`Price or Item ID for addon "${addon}" is missing.`);
                }
            }
        }

        // Pushed after the add-on branches so the "Have It All" filter above can't drop it.
        if (userCount > 1) {
            this.selectedItemsForCheckout.push({
                item_price_id: AdditionalUserItemIdMap[this.billingFrequency],
                quantity: userCount - 1
            });
        }

        // Add-ons still multiply by seat count on legacy multi-user subscriptions; seats themselves
        // are a flat rate per extra user on every plan.
        const seatTotal = seatTotalForPeriod(basePrice, userCount, this.billingFrequency);
        this.billedTotalPrice = isExistingMultiSubscription
            ? seatTotal + additionalPrice * userCount
            : seatTotal + additionalPrice;
        this.totalPrice = Math.round(this.billedTotalPrice / periodMonths);
        const existing_items_list = this.currentSubscription?.subscription_items?.map(item => item.item_price_id).sort().join(',') ?? '';
        const selected_items_list = this.selectedItemsForCheckout.map(item => item.item_price_id).sort().join(',');

        // check to see if this is different from what they already have
        if (subscriptionInfo.number_of_seats !== userCount || existing_items_list !== selected_items_list) {
            this.isChanged = true;
        } else {
            this.isChanged = false;
        }


        try {
            await this.getLoadedEstimate();
        } catch (e) {
            console.error(e);
        }
    }

    async addAddon(addon: StandardProAddOns) {
        if (addon === StandardProAddOns.HaveItAll) {
            // Premium upsell: select the bundle, not an à-la-carte line.
            this.premiumPreselected = true;
            this.planType = 'haveItAll';
            this.selectedStandardAddOns = [StandardProAddOns.HaveItAll];
        } else if (!this.selectedStandardAddOns.includes(addon)) {
            this.selectedStandardAddOns.push(addon);
        }
        this.requestUpdate();
        await this.updatePricing();
    }

    private async checkout() {
        this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
        try {
            logEventWithKlayvio(KlayvioEvents.CheckoutClicked, accountInfo.email, [
                { key: KlavioEventPropertyKey.UpgradeTier, value: this.planType ?? '' },
            ]);
        } catch (error) {
            console.error(error);
        }

        /// REMOVING FOR NOW TO SEE IF IT UPS THE CONVERSION RATE
        // // if user is not authenticated yet, we need to have them log in first so their organization can be set
        // if (!accountInfo.isAuthenticated) {
        //     // open login modal page
        //     const qs = new URLSearchParams();
        //     qs.set('email', getFromLocalStorage('email') ?? undefinedIfEmpty(accountInfo.email) ?? topicData?.topic?.email ?? '');
        //     qs.set('source', 'checkout');

        //     const loginUrl =`${window.location.origin}/#login?${qs.toString()}`;

        //     // open in current tab
        //     window.location.href = loginUrl;
        //     return;
        // }

        await checkoutSubscription(this.selectedItemsForCheckout, (error, result) => {
            if (isDefined(error)) {
                console.error(error);
            } else if (result === 'success') {
                void subscriptionInfo.refreshSubscriptionInfo();
                void show_confetti();
            } else if (result === 'closed') {
                // do nothing
            } else if (result === 'urlSet') {
                if (detectMobileViewport() || this.button_only || this.in_embed) {
                    // get checkout url from local storage
                    let checkoutUrl = localStorage.getItem('checkout_url') ?? '';
                    let attempts = 0;
                    const checkUrl = () => {
                        checkoutUrl = localStorage.getItem('checkout_url') ?? '';
                        if (checkoutUrl === '' && attempts < 3) {
                            setTimeout(checkUrl, 1000); // wait for 1 second
                            attempts++;
                        } else if (checkoutUrl !== '') {
                            // open in new tab
                            window.open(checkoutUrl, '_blank');
                            // end this session
                            localStorage.removeItem('checkout_url');
                            // close this one
                            const cbInstance = (window as any).Chargebee.getInstance();
                            cbInstance.closeCheckout();
                        }
                    };
                    checkUrl();
                };
            }
        });
    }

    render() {
        const isWebEmbed = inIframe() && detectMobileViewport();
        if (isWebEmbed) {
            this.showAllAddons = true;
        }
        return (this.mini || isWebEmbed) ? this.renderMini() : this.button_only ? this.renderButton() : this.renderFullSized();
    }

    /** "Return here" means nothing on a customer's own site, where there's no account page to return to. */
    private get trialCancelNotice(): string {
        return this.in_embed || inIframe()
            ? 'Billing begins on the 7th day. Log in to cancel before your trial ends to avoid charges.'
            : 'Billing begins on the 7th day. Return here to cancel before your trial ends to avoid charges.';
    }

    /** Shared by all four total layouts so the discount copy stays identical across them. */
    private renderNonprofitNote() {
        const status = this.currentSubscription?.status;
        return html`
            <span class="pricing-tab-button-starting nonprofit-note">
                ${renderNonprofitDiscountNote('cart-component', {
                    monthlyTotal: this.totalPrice,
                    billedTotal: this.billedTotalPrice,
                    billedPeriodLabel: this.billingFrequency === BillingOption.Annual ? 'annually'
                        : this.billingFrequency === BillingOption.Quarterly ? 'every 3 months' : undefined,
                    hasExistingSub: status === 'in_trial' || status === 'active' || status === 'cancelled',
                })}
            </span>
        `;
    }

    renderFullSized() {
        const isInTrial = this.currentSubscription?.status === 'in_trial';
        const isActive = this.currentSubscription?.status === 'active';
        const isCancelled = this.currentSubscription?.status === 'cancelled';
        return html`
        <div class="users-and-billing-section">
            ${this.in_embed || isDefined(organizationInfo.organization) ? html`
                <div class="user-controls">
                    <span style="font-weight:700">Number of Users</span>
                    <div class="user-controls-holder">
                        <span class="user-controls-count" id="userCount">${this.userCount}</span>
                        <div class="user-controls-toggle" @click=${async () => await this.changeUserCount(-1)}>-</div>
                        <div class="user-controls-toggle" @click=${async () => await this.changeUserCount(1)}>+</div>
                    </div>
                </div>
            ` : ''}
            <div style="display: flex; flex-direction: row; align-items: center; gap: 10px;">
                <span style="font-weight:700">Billing Term</span>
                <div class="discount-toggle-container">

                    <span class="${this.billingFrequency === BillingOption.Annual ? 'active' : ''} discount-toggle-button" id="yearlyToggle" @click=${async () => await this.updateToggleState(BillingOption.Annual)}>Yearly</span>
                    <span class="${this.billingFrequency === BillingOption.Quarterly ? 'active' : ''} discount-toggle-button" id="quarterlyToggle" @click=${async () => await this.updateToggleState(BillingOption.Quarterly)}>Seasonal</span>
                    <span class="${this.billingFrequency === BillingOption.Monthly ? 'active' : ''} discount-toggle-button" id="monthlyToggle" @click=${async () => await this.updateToggleState(BillingOption.Monthly)}>Monthly</span>
                </div>
            </div>
        </div>
        <div class="container">
            <div class="column">
                <div class="cart-container" style="flex-direction: row; align-items: stretch;">
                    <div class="addons-container">
                        <h4 class="addons-title">Your plan</h4>
                        <div class="feature-item">
                            <input checked disabled type="checkbox">
                            <label class="feature-label">Proxi Pro</label>
                            <span class="right-justified" id="priceCustomForm">$${this.billingFrequency === BillingOption.Monthly ? FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Monthly :
                                    this.billingFrequency === BillingOption.Annual ? Math.round((FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Annual ?? 1)/12) :
                                    Math.round((FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Quarterly ?? 1)/3)}<span class="price-unit">/mo</span></span>
                        </div>
                        <p class="info-text">Basic branded access to maps, calendar, directory, and bulletins. Usage limits apply.</p>
                    </div>
                    <div class="addons-container ${this.planType === 'haveItAll' ? 'plan-card-selected' : ''}">
                        <h4 class="addons-title">Go further <span style="font-weight:400;font-size:0.7rem;color:#777;">(per team)</span> <span class="most-popular-badge">Most Popular</span></h4>
                        <div class="feature-item">
                            <input ?checked=${this.planType === 'haveItAll'} type="checkbox" @click=${this.updatePlanToggleState}>
                            <label class="feature-label" for="premiumToggle">Upgrade to Premium</label>
                            <span class="right-justified ${this.planType === 'haveItAll' ? '' : 'light-gray'}" id="priceCustomForm">+$${Math.round(this.billingFrequency === BillingOption.Monthly ? AddOnMonthlyPriceMap[StandardProAddOns.HaveItAll].Monthly :
                                    this.billingFrequency === BillingOption.Annual ? Math.round(AddOnMonthlyPriceMap[StandardProAddOns.HaveItAll].Annual/12) :
                                    Math.round(AddOnMonthlyPriceMap[StandardProAddOns.HaveItAll].Quarterly/3))}<span class="price-unit">/mo</span></span>
                        </div>
                        <p class="info-text">Unlock higher usage across your maps and calendar, plus the tools to market your map and measure results: gamified experiences (Guides, Passports, Scavenger Hunts, Best Of), audience messaging, analytics, sponsorships, pop-ups, API access, and custom domains. (Formerly Have It All.)</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="total-price-container">
            <div class="pricing-tab-button-total">
                <span class="pricing-tab-button-price">
                    <span class="pricing-tab-button-cost" id="totalPrice">$${this.totalPrice}</span>
                    <span class="pricing-tab-button-cost">per month</span>
                </span>
                <span class="pricing-tab-button-starting">${this.totalPrice > 0 ? html`for ${this.userCount === 1 ? '1 workspace member' : `${this.userCount} teammates`}` : ''}</span>
                <span class="pricing-tab-button-starting">${this.billingFrequency === BillingOption.Quarterly ? html`$${this.billedTotalPrice} billed Every 3 Months` :
                    this.billingFrequency === BillingOption.Annual ? html`$${this.billedTotalPrice} billed annually` :
                ''}</span>
                ${this.renderNonprofitNote()}
                <br>
                ${this.isChanged || isCancelled ? html`
                    <form-button size="md" class="button" backgroundColor="var(--main-primary-color)" @click="${async () => await this.checkout()}">
                        ${isInTrial || isActive ? `Update ${isInTrial ? 'Trial' : 'Subscription'}` : isCancelled ? 'Resubscribe' : 'Start 7 Day Free Trial'}
                    </form-button>
                    ${this.estimate && (isInTrial || isActive || isCancelled) ?
                        html`
                            <div>
                                <span>Due Today: $${this.estimate.due_today/100}
                                ${this.estimate.credits && this.estimate.credits > 0 ? html`<br>($${this.estimate.credits/100} prorated credits applied)` :''}</span>
                            </div>
                        ` : html`
                        <div style="margin-top:10px;">
                            <span>${this.trialCancelNotice}</span>
                        </div>
                        `
                    }
                ` : ''}
            </div>
        </div>
        `;
    }

    renderMini() {
        const isInTrial = this.currentSubscription?.status === 'in_trial';
        const isActive = this.currentSubscription?.status === 'active';
        const isCancelled = this.currentSubscription?.status === 'cancelled';
        return html`
        <div class="users-and-billing-section miniFont">
            <div style="display: flex; flex-direction: row; align-items: center; gap: 10px; flex: 1;">
                <div class="discount-toggle-container discount-toggle-container-mini miniFont">
                    <span class="${this.billingFrequency === BillingOption.Annual ? 'active' : ''} discount-toggle-button discount-toggle-button-mini" id="yearlyToggle" @click=${async () => await this.updateToggleState(BillingOption.Annual)}>Yearly</span>
                    <span class="${this.billingFrequency === BillingOption.Quarterly ? 'active' : ''} discount-toggle-button discount-toggle-button-mini" id="quarterlyToggle" @click=${async () => await this.updateToggleState(BillingOption.Quarterly)}>Seasonal</span>
                    <span class="${this.billingFrequency === BillingOption.Monthly ? 'active' : ''} discount-toggle-button discount-toggle-button-mini" id="monthlyToggle" @click=${async () => await this.updateToggleState(BillingOption.Monthly)}>Monthly</span>
                </div>
            </div>
        </div>
        <div class="container miniFont">
            <div class="columnMini">
                <div class="cart-container">
                    <div class="addons-container miniFont">
                        <h4 class="addons-title addons-title-mini miniFontHeader">Subscription</h4>
                        <div class="feature-item feature-item-mini">
                            <input checked disabled type="checkbox">
                            <label class="feature-label">Proxi Pro</label>
                            <span class="right-justified" id="priceCustomForm">$${this.billingFrequency === BillingOption.Monthly ? FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Monthly
                                : this.billingFrequency === BillingOption.Annual ? Math.round((FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Annual ?? 1)/12)
                                : Math.round((FeatureTierMonthlyPriceMap[FeatureTierLevel.ProBase].Quarterly ?? 1)/3)}<span class="price-unit">/mo</span>
                            </span>
                        </div>
                    </div>
                    <div class="addons-container miniFont ${this.planType === 'haveItAll' ? 'plan-card-selected' : ''}">
                        <h4 class="addons-title addons-title-mini miniFontHeader">Go further</h4>
                        <div class="feature-item feature-item-mini">
                            <input ?checked=${this.planType === 'haveItAll'} type="checkbox" @click=${this.updatePlanToggleState}>
                            <label class="feature-label" for="premiumToggle">Upgrade to Premium</label>
                            <span class="right-justified ${this.planType === 'haveItAll' ? '' : 'light-gray'}" id="priceCustomForm">+$${Math.round(this.billingFrequency === BillingOption.Monthly ? AddOnMonthlyPriceMap[StandardProAddOns.HaveItAll].Monthly :
                                this.billingFrequency === BillingOption.Annual ? AddOnMonthlyPriceMap[StandardProAddOns.HaveItAll].Annual/12 : AddOnMonthlyPriceMap[StandardProAddOns.HaveItAll].Quarterly/3)}<span class="price-unit">/mo</span></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="total-price-container total-price-container-mini">
            <div class="pricing-tab-button-total" style="align-items:center">
                <span class="pricing-tab-button-price">
                    <span class="pricing-tab-button-cost miniFontHeader" id="totalPrice">$${this.totalPrice}</span>
                    <span class="pricing-tab-button-cost miniFontHeader">per month</span>
                </span>
                <span class="miniFont">
                    For ${this.userCount === 1 ? '1 workspace member' : `${this.userCount} teammates`}
                </span>
                <span class="pricing-tab-button-starting miniFont">${this.billingFrequency === BillingOption.Annual ? html`$${this.billedTotalPrice} billed annually` :
                    this.billingFrequency === BillingOption.Quarterly ? html`$${this.billedTotalPrice} billed Every 3 Months` : ''}
                </span>
                ${this.renderNonprofitNote()}
                <br>
                ${this.isChanged ? html`
                    <form-button size="md" class="button" backgroundColor="var(--main-primary-color)" @click="${async () => await this.checkout()}">
                        ${isInTrial || isActive ? isInTrial ? 'Update Trial' : 'Update Features' : isCancelled ? 'Restart' : 'Start 7 Day Trial'}
                    </form-button>
                    ${this.estimate && (isInTrial || isActive || isCancelled) ?
                        html`
                            <div>
                                <span class="miniFont">Due Today: $${this.estimate.due_today/100}
                                ${this.estimate.credits && this.estimate.credits > 0 ? html`<br>($${this.estimate.credits/100} prorated credits applied)` :''}</span>
                            </div>
                        ` : html`
                        <div>
                            <span class="miniFont">Billing begins on 7th day. Return to Proxi to cancel before trial ends to avoid charges.</span>
                        </div>
                        `
                    }
                ` : ''}
            </div>
        </div>
        `;
    }

    renderButton() {
        const isInTrial = this.currentSubscription?.status === 'in_trial';
        const isActive = this.currentSubscription?.status === 'active';
        const isCancelled = this.currentSubscription?.status === 'cancelled';
        // if the user is logged in and has more than proxi pro, say they need to log in to upgrade
        if (accountInfo.isAuthenticated && (this.currentSubscription?.subscription_items?.length ?? 0) > 1 && this.in_embed) {
            return html`
                <div class="users-and-billing-section" style="justify-content:center">
                    Log in to upgrade your features ${organizationInfo.organization?.chargebee_subscription ? 'for your team (' + organizationInfo.organization?.name + ')' : `for ${accountInfo.email}`}
                </div>
            `;
        }
        return html`
            <div class="users-and-billing-section" style="justify-content:center">
                ${this.in_embed || isDefined(organizationInfo.organization) ? html`
                    <div class="user-controls">
                        <span style="font-weight:700">Number of Users</span>
                        <div class="user-controls-holder-button">
                            <span class="user-controls-count-button" id="userCount">${this.userCount}</span>
                            ${this.userCount === this.currentNumUsers || this.userCount > this.currentNumUsers ? html`
                                <span style="font-size: 18px;font-weight: bold;cursor: pointer;" @click=${async () => await this.changeUserCount(-1)}>-</span>
                            ` : ''}
                            <span style="font-size: 18px;font-weight: bold;cursor: pointer;" @click=${async () => await this.changeUserCount(1)}>+</span>
                        </div>
                    </div>
                ` : ''}
            </div>
            <div class="total-price-container">
                <div class="pricing-tab-button-total" style="align-items:center">
                    <span class="pricing-tab-button-price" style="text-align:center;display:block">
                        <b><span class="pricing-tab-button-cost" id="totalPrice">$${this.totalPrice}</b>
                            <span class="pricing-tab-button-cost">
                                per month for ${this.userCount === 1 ? '1 workspace member' : `${this.userCount} teammates`}</span>
                        </span>
                    </span>
                    <span class="pricing-tab-button-starting">${this.billingFrequency === BillingOption.Annual ? html`$${this.billedTotalPrice} billed annually` :
                        this.billingFrequency === BillingOption.Quarterly ? html`$${this.billedTotalPrice} billed Every 3 Months` : ''}
                    </span>
                    ${this.renderNonprofitNote()}
                    <form-button size="lg" class="button" backgroundColor="var(--main-primary-color)" @click="${async () => await this.checkout()}">
                        ${isInTrial ||isActive ? 'Update' : isCancelled ? 'Resubscribe' : 'Start 7 Day Trial'} Now
                    </form-button>
                    ${this.estimate && (isInTrial || isActive || isCancelled) ?
                        html`
                            <div>
                                <span>Due Today: $${this.estimate.due_today/100}</span>
                            </div>
                        ` :
                        html`
                            <div>
                                <span>Billing begins on 7th day. Return to Proxi to cancel before trial ends to avoid charges.</span>
                            </div>
                        `
                    }
                </div>
            </div>
        `;

    }

    renderChangeNumUsers() {
        const isInTrial = this.currentSubscription?.status === 'in_trial';
        const isActive = this.currentSubscription?.status === 'active';
        const isCancelled = this.currentSubscription?.status === 'cancelled';
        return html`
            <div class="users-and-billing-section" style="justify-content:center">
                ${this.in_embed || isDefined(organizationInfo.organization) ? html`
                    <div class="user-controls">
                        <span style="font-weight:700">Number of Teammates</span>
                        <div class="user-controls-holder-button">
                            <span class="user-controls-count-button" id="userCount">${this.userCount}</span>
                            ${this.userCount === this.currentNumUsers ? html`
                                <span style="font-size: 18px;font-weight: bold;cursor: pointer;" @click=${async () => await this.changeUserCount(-1)}>-</span>
                            ` : ''}
                            <span style="font-size: 18px;font-weight: bold;cursor: pointer;" @click=${async () => await this.changeUserCount(1)}>+</span>
                        </div>
                    </div>
                ` : ''}
            </div>
            <div class="total-price-container">
                <div class="pricing-tab-button-total" style="align-items:center">
                    <span class="pricing-tab-button-price">
                        <span class="pricing-tab-button-cost" id="totalPrice">$${this.totalPrice}</span>
                        <span class="pricing-tab-button-cost">per month</span>
                    </span>
                    <span class="pricing-tab-button-starting">${this.billingFrequency === BillingOption.Annual ? html`$${this.billedTotalPrice} billed annually` :
                        this.billingFrequency === BillingOption.Quarterly ? html`$${this.billedTotalPrice} billed Every 3 Months` : ''}
                    </span>
                    ${this.renderNonprofitNote()}
                    <br>
                    <form-button size="md" class="button" backgroundColor="var(--main-primary-color)" @click="${async () => await this.checkout()}">
                        ${isInTrial || isActive ? 'Update' : isCancelled ? 'Resubscribe' : 'Start 7 Day Trial'} Now
                    </form-button>
                    ${this.estimate && (isInTrial || isActive || isCancelled) ?
                        html`
                            <div>
                                <span>Due Today: $${this.estimate.due_today/100}</span>
                            </div>
                        ` : html`
                        <div style="margin-top:10px;">
                            <span>Billing begins on 7th day. Return to Proxi to cancel before trial ends to avoid charges.</span>
                        </div>
                        `
                    }
                </div>
            </div>
        `;

    }



    static get styles() {
        return css`
            .container {
                color: black;
                font-family: Montserrat, sans-serif;
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                flex: 1;
                justify-content: space-between;
            }
            .column {
                flex: 2;
                padding: 10px;
            }
            .column1 {
                font-size: 16px;
            }
            .toggle-container {
                display: flex;
                flex-direction: column;
            }
            .plan-toggle,
            .toggle {
                padding: 5px 10px;
                border: 1px solid #5ea667;
                border-radius: 5px;
                margin-bottom: 10px;
                cursor: pointer;
                display: inline-block;
            }
            .plan-toggle-container {
                margin-bottom: 20px;
            }
            .plan-subtitle {
                font-size: 14px;
                margin-bottom: 5px;
            }
            .columnMini {
                width: 100%;
            }
            .user-controls {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                gap: 10px;
                font-size: 1rem;
            }
            .info-text {
                padding: 0 10px;
                font-size: 14px;
            }
            .cart-container {
                display: flex;
                flex-direction: column;
                gap: 10px;
                align-items: flex-start;
            }
            .pricing-tab-button-price {
                display: flex;
                flex-direction: row;
                gap: 5px;
            }
            .user-controls-holder {
                border-radius: 5px;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 2px;
            }
            .user-controls-holder-button {
                gap: 5px;
                margin-bottom: 0px;
            }
            .user-controls-count {
                flex-grow: 1;
                font-size: 20px;
                user-select: none;
                border-radius: 4px;
            }
            .user-controls-count-button {
                padding: 0;
                border: none;
                margin-right: 8px;
            }
            .user-controls-toggle {
                flex-grow: 1;
                font-size: 20px;
                background: 0px 0px;
                border-radius: 50%;
                cursor: pointer;
                height: 20px;
                width: 20px;
                text-align: center;
                display: flex;
                justify-content: center;
                align-items: center;
                background: rgb(243, 244, 246);
            }
            .addons-container {
                border: 1px solid #aaa;
                border-radius: 5px;
                flex:1;
                width: 100%;
            }
            .plan-card-selected {
                border: 2px solid var(--main-success-color);
                background: #f3fbf4;
            }
            .most-popular-badge {
                background: #dbeafe;
                color: #1e40af;
                font-size: 0.6rem;
                font-weight: 700;
                padding: 2px 8px;
                border-radius: 10px;
                vertical-align: middle;
                margin-left: 6px;
            }
            .addons-title,
            .settings-title {
                text-align: center;
                font-weight: 700;
                margin: 0;
                padding: 10px 15px;
            }

            .addons-title-top-border {
                border-top: 1px solid #aaa;
            }

            .addons-title-mini {
                padding: 5px 10px;
            }
            .price-unit {
                font-weight: 400;
                font-size: 0.75rem;
                color: #777;
            }
            .right-justified {
                text-align: right;
                font-weight: 700;
            }
            .light-gray {
                font-weight: 400;
                color: #aaa;
            }
            .total-price-container {
                text-align: right;
            }
            .total-price-container-mini {
                text-align: center;
            }
            .feature-item {
                display: flex;
                align-items: center;
                border-top: 1px solid #aaa;
                padding: 10px;
            }
            .feature-item > input:checked {
                color: red;
            }
            .feature-item-mini {
                padding: 2px;
            }
            .feature-label {
                flex-grow: 1;
                margin-left: 10px;
                color: rgb(85, 85, 85);
                display: flex;
                gap: 5px;
            }

            .pricing-tab-menu {
                display: flex;
                flex-direction: row;
                gap: 10px;
                user-select: none;
            }
            .pricing-tab-button {
                border: 1px solid #aaa;
                border-radius: 5px;
                margin-bottom: 10px;
                cursor: pointer;
                flex: 1;
            }
            .pricing-tab-button-title {
                font-weight: 700;
                border-bottom: 1px solid #aaa;
                padding: 10px 15px;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
            }
            .pricing-tab-button-detail {
                padding: 10px;
                display: flex;
                flex-direction: column;
            }
            .pricing-tab-button-total {
                padding: 10px;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                align-items: flex-end;
                font-size: 1rem;
            }
            .pricing-tab-button-starting {
                display: block;
                color: #555;
                margin: 0;
                padding: 0;
            }
            .nonprofit-note {
                font-size: 0.7rem;
                line-height: 1.35;
                margin-top: 0.5rem;
                max-width: 22rem;
            }
            .nonprofit-note .link {
                color: inherit;
            }
            .pricing-tab-button {
                border: 3px solid var(--main-moon-color);
            }
            .pricing-tab-button.active {
                border: 3px solid var(--main-success-color);
            }
            .pricing-tab-button .pricing-tab-button-title {
                border-bottom: 3px solid var(--main-moon-color);
            }
            .pricing-tab-button.active .pricing-tab-button-title {
                border-bottom: 3px solid #5ea667;
                background: #e9faeb;
            }
            .discount-toggle-container {
                background-color: #f3f4f6;
                border-radius: 99px;
                justify-content: center;
                align-items: center;
                padding: 4px;
                display: flex;
                position: relative;
                width: fit-content;
                user-select: none;
            }
            .discount-toggle-button {
                grid-column-gap: 4px;
                grid-row-gap: 4px;
                color: #4b5563;
                background-color: #f3f4f6;
                border-radius: 44px;
                justify-content: center;
                align-items: center;
                padding-left: 16px;
                padding-right: 16px;
                font-weight: 500;
                line-height: 32px;
                display: flex;
                vertical-align: top;
                text-align: center;
                cursor: pointer;
                color: #222;
                text-decoration: none;
                display: inline-block;
                position: relative;
                font-weight: 700;
            }
            .discount-toggle-button.active {
                border-radius: 100px;
                background: #fff;
            }

            .discount-toggle-button-mini {
                padding-left: 8px;
                padding-right: 8px;
                font-weight: 500;
                line-height: 14px;
                border-radius: 4px;
                flex:1;
            }

            .discount-toggle-button-mini.active {
                border-radius: 4px;
                background: var(--main-featured-color);
            }

            .discount-toggle-container-mini {
                border-radius: 4px;
                margin-bottom: 0.5rem;
                flex: 1;
            }

            .users-and-billing-section {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
            }

            .miniFont {
                font-size: 12px;
                line-height: 14px;
                width: 100%;
            }

            .miniFontHeader {
                font-size: 14px;
                font-weight: 600;
            }
        `;
    }
}
